// =============================================================================
// io/sync/propTemplates.ts — saving and deleting the trader's own rule templates.
// -----------------------------------------------------------------------------
// `user_preferences.prop_templates` is one JSONB column holding an ARRAY, and
// that changes what "a patch is safe" means.
//
// ── 🚨 THE PATCH GUARANTEE DOES NOT COVER A COLLECTION ──
//
// `io/sync/preferences.ts` argues at length that saves must be patches, so a
// screen cannot revert a field it never showed. That holds — for scalar fields.
// It does not help here: appending to an array is a READ-MODIFY-WRITE, and the
// value being written is the whole list. If the list came from a snapshot the
// browser loaded an hour ago, saving a template silently reverts every template
// saved since, from any tab or device.
//
// So this module reads the column FRESH, immediately before writing it. The
// window shrinks from "however long this tab has been open" to the round trip.
// It is not a lock and it does not pretend to be one — but the old client writes
// its whole in-memory `state.settings`, so the difference is hours versus
// milliseconds.
//
// ── 🚨 A FAILED READ MUST NEVER BECOME AN EMPTY LIST ──
//
// The one failure that destroys data rather than merely failing: treat a read
// error as "no templates yet", append one, and the write wipes the rest. Every
// path below distinguishes "the query succeeded and found no row" (a genuinely
// new user — an empty list is correct) from "the query failed" (refuse).
//
// ── ⚠ AND THE ENTRIES ARE PASSED THROUGH RAW ──
//
// The list is edited by `domain/accountSetup`'s raw-array helpers, which read
// `id` and `label` and otherwise never look inside a stored entry. Parsing the
// list into typed templates and writing those back would drop any key the parser
// does not model — on templates the trader never touched. D62, one level down.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import {
  propTemplateIsComplete, removePropTemplate, upsertPropTemplate,
  type PropTemplate,
} from '../../domain/accountSetup';
import { reportFailureAs } from '../diagnostics/errorLog';

export interface TemplateWriteResult {
  ok: boolean;
  error: string | null;
  /** The list as written. Null when nothing was written. */
  templates: unknown[] | null;
  /**
   * Set when the save REPLACED a template of the same name rather than adding
   * one. The dialog says so afterwards — a save that quietly overwrote something
   * is the kind of surprise that gets found later, by its absence.
   */
  replacedId?: string | null;
}

export interface TemplateWriteOptions {
  /** Build the list and report it without writing. Used by the round-trip test. */
  dryRun?: boolean;
}

/**
 * Read the stored array, freshly.
 *
 * Returns `null` on a read FAILURE, and `[]` only when the query succeeded and
 * the user has no preferences row. The caller must not conflate them.
 *
 * ⚠ `scope` is passed IN rather than fixed here because both helpers serve the
 * save path and the delete path. A log full of lines that all read the same is
 * no better than no log — "the delete failed" and "the save failed" are the
 * first thing anyone reading it needs to tell apart.
 */
async function readStored(
  sb: SupabaseClient,
  userId: string,
  scope: string,
): Promise<{ list: unknown[] } | { error: string }> {
  const { data, error } = await sb
    .from('user_preferences')
    .select('prop_templates')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) return { error: reportFailureAs(scope, error, error.message) };
  if (!data) return { list: [] };
  const raw = (data as Record<string, unknown>)['prop_templates'];
  return { list: Array.isArray(raw) ? raw : [] };
}

/** Write the column back. Upsert, so a user with no row yet still gets one. */
async function writeStored(
  sb: SupabaseClient,
  userId: string,
  list: unknown[],
  scope: string,
): Promise<string | null> {
  const { error } = await sb
    .from('user_preferences')
    .upsert({ user_id: userId, prop_templates: list }, { onConflict: 'user_id' });
  return error ? reportFailureAs(scope, error, error.message) : null;
}

/**
 * Save a template, replacing any existing one of the same name.
 *
 * ⚠ REFUSES AN INCOMPLETE TEMPLATE. `buildPropTemplate` fills every required
 * key, so reaching this is a bug in the filler rather than bad user input — and
 * an incomplete template is not a cosmetic problem: it prefills an account with
 * gaps that two engines then default in opposite directions.
 */
export async function savePropTemplate(
  sb: SupabaseClient,
  userId: string,
  tpl: PropTemplate,
  opts: TemplateWriteOptions = {},
): Promise<TemplateWriteResult> {
  if (!userId) {
    return { ok: false, error: 'Not signed in — the template was not saved.', templates: null };
  }
  if (!propTemplateIsComplete(tpl)) {
    return {
      ok: false,
      templates: null,
      error: 'That template’s rule set came out incomplete, so it was not saved. '
        + 'Nothing about the account has changed.',
    };
  }

  const stored = await readStored(sb, userId, 'save prop template');
  if ('error' in stored) {
    // 🚨 REFUSE. See the header — appending to an assumed-empty list is how the
    // rest of the trader's templates get erased by a network blip.
    return {
      ok: false,
      templates: null,
      error: `Couldn’t read your saved templates, so nothing was written: ${stored.error}`,
    };
  }

  const { next, replacedId } = upsertPropTemplate(stored.list, tpl);
  if (opts.dryRun) return { ok: true, error: null, templates: next, replacedId };

  const error = await writeStored(sb, userId, next, 'save prop template');
  if (error) return { ok: false, error, templates: null };
  return { ok: true, error: null, templates: next, replacedId };
}

/**
 * Delete a saved template.
 *
 * ⚠ ACCOUNTS MADE FROM IT ARE UNAFFECTED, and the UI has to say so. A template
 * is a starting shape that was copied at creation time; nothing reads it
 * afterwards. Deleting one cannot change a single gauge — which is exactly why
 * it is safe to offer without a confirmation step.
 */
export async function deletePropTemplate(
  sb: SupabaseClient,
  userId: string,
  id: string,
  opts: TemplateWriteOptions = {},
): Promise<TemplateWriteResult> {
  if (!userId || !id) {
    return { ok: false, error: 'Nothing to delete.', templates: null };
  }

  const stored = await readStored(sb, userId, 'delete prop template');
  if ('error' in stored) {
    return {
      ok: false,
      templates: null,
      error: `Couldn’t read your saved templates, so nothing was deleted: ${stored.error}`,
    };
  }

  const { next, removed } = removePropTemplate(stored.list, id);
  if (!removed) {
    // ⚠ NOT a silent success. It means someone else already deleted it, or the id
    // is wrong — and reporting "deleted" for a row that was never there is how a
    // list that refuses to shrink looks like a UI bug instead of a stale tab.
    return {
      ok: false,
      templates: null,
      error: 'That template is no longer there — reload and try again.',
    };
  }
  if (opts.dryRun) return { ok: true, error: null, templates: next };

  const error = await writeStored(sb, userId, next, 'delete prop template');
  if (error) return { ok: false, error, templates: null };
  return { ok: true, error: null, templates: next };
}
